import { useState } from "react";
import Modal from "@mui/material/Modal";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import api from "services/api";
import { useTranslation } from "react-i18next";

export default function AppelModal({ open, onClose, reunion, onUpdated }) {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [closing, setClosing] = useState(false);

  if (!reunion) return null;
  const convocations = reunion.convocations || [];

  const setPresence = async (user_id, statut_presence) => {
    setSaving(true);
    try {
      await api.put(`/reunions/${reunion.id}/presence`, { user_id, statut_presence });
      await onUpdated();
    } catch (err) {
      alert(err.response?.data?.message || t("commun.erreur"));
    }
    setSaving(false);
  };

  const cloturer = async () => {
    if (!window.confirm(t("reunions.appel.confirmerCloture"))) return;
    setClosing(true);
    try {
      await api.put(`/reunions/${reunion.id}`, {
        titre: reunion.titre,
        date: reunion.date,
        heure_debut: reunion.heure_debut,
        heure_fin: reunion.heure_fin,
        lieu: reunion.lieu,
        ordre_du_jour: reunion.ordre_du_jour,
        statut: "terminee",
      });
      await onUpdated();
      onClose();
    } catch (err) {
      alert(err.response?.data?.message || t("commun.erreur"));
    }
    setClosing(false);
  };

  const nbPointes = convocations.filter(c => ["present", "absent"].includes(c.statut_presence)).length;
  const nbPresents = convocations.filter(c => c.statut_presence === "present").length;
  const nbAbsents  = convocations.filter(c => c.statut_presence === "absent").length;

  return (
    <Modal open={open} onClose={onClose} sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>
      <MDBox sx={{
        background: "#fff", borderRadius: "16px", width: "100%", maxWidth: 720,
        maxHeight: "92vh", display: "flex", flexDirection: "column", outline: "none",
      }}>
        {/* Header */}
        <MDBox sx={{
          background: "linear-gradient(135deg, #1565c0, #1976d2)", color: "#fff",
          borderRadius: "16px 16px 0 0", p: 3,
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <MDBox>
            <MDTypography variant="h5" sx={{ color: "#fff" }}>
              📋 {t("reunions.appel.titre")}
            </MDTypography>
            <MDTypography variant="body2" sx={{ color: "rgba(255,255,255,0.9)" }}>
              {reunion.titre}
            </MDTypography>
          </MDBox>
          <MDBox sx={{ cursor: "pointer" }} onClick={onClose}>
            <Icon sx={{ fontSize: 32, color: "#fff" }}>close</Icon>
          </MDBox>
        </MDBox>

        {/* Compteurs */}
        <MDBox sx={{ background: "#f8faff", p: 2, display: "flex", justifyContent: "space-around", borderBottom: "1px solid #e0e0e0" }}>
          <MDBox textAlign="center">
            <MDTypography variant="h4" fontWeight="bold" sx={{ color: "#1565c0" }}>{nbPointes} / {convocations.length}</MDTypography>
            <MDTypography variant="caption" color="text">{t("reunions.appel.pointes")}</MDTypography>
          </MDBox>
          <MDBox textAlign="center">
            <MDTypography variant="h4" fontWeight="bold" sx={{ color: "#2e7d32" }}>{nbPresents}</MDTypography>
            <MDTypography variant="caption" color="text">{t("reunions.appel.presents")}</MDTypography>
          </MDBox>
          <MDBox textAlign="center">
            <MDTypography variant="h4" fontWeight="bold" sx={{ color: "#c62828" }}>{nbAbsents}</MDTypography>
            <MDTypography variant="caption" color="text">{t("reunions.appel.absents")}</MDTypography>
          </MDBox>
        </MDBox>

        {/* Liste */}
        <MDBox sx={{ overflowY: "auto", flex: 1, p: 2 }}>
          {convocations.length === 0 ? (
            <MDTypography variant="body2" textAlign="center" color="text">{t("reunions.aucunConvoque")}</MDTypography>
          ) : convocations.map((c) => {
            const isPresent = c.statut_presence === "present";
            const isAbsent  = c.statut_presence === "absent";
            return (
              <MDBox key={c.user_id} sx={{
                display: "flex", alignItems: "center", gap: 1.5,
                p: 2, mb: 1, borderRadius: "10px",
                background: isPresent ? "#e8f5e9" : isAbsent ? "#ffebee" : "#f8faff",
                border: `2px solid ${isPresent ? "#2e7d32" : isAbsent ? "#c62828" : "transparent"}`,
                transition: "all 0.15s",
              }}>
                <MDBox sx={{ flex: 1, minWidth: 0 }}>
                  <MDTypography variant="body1" fontWeight="bold" color="dark">
                    {c.enseignant?.prenom} {c.enseignant?.nom}
                  </MDTypography>
                  <MDTypography variant="caption" color="text" sx={{ fontSize: 11 }}>
                    {c.statut_presence === "confirme" && `✅ ${t("reunions.appel.avaitConfirme")}`}
                    {c.statut_presence === "refuse" && `❌ ${t("reunions.appel.avaitRefuse")}`}
                    {c.statut_presence === "en_attente" && `⏳ ${t("reunions.appel.pasRepondu")}`}
                    {c.statut_presence === "present" && `✓ ${t("reunions.appel.marquePresent")}`}
                    {c.statut_presence === "absent" && `✗ ${t("reunions.appel.marqueAbsent")}`}
                  </MDTypography>
                </MDBox>
                <MDButton size="medium" disabled={saving}
                  onClick={() => setPresence(c.user_id, "present")}
                  sx={{
                    background: isPresent ? "#2e7d32" : "#fff",
                    color: isPresent ? "#fff" : "#2e7d32",
                    border: "2px solid #2e7d32", fontWeight: 700, minWidth: 100,
                  }}>
                  <Icon sx={{ mr: 0.5 }}>check</Icon> {t("valeurs.statutPresence.present")}
                </MDButton>
                <MDButton size="medium" disabled={saving}
                  onClick={() => setPresence(c.user_id, "absent")}
                  sx={{
                    background: isAbsent ? "#c62828" : "#fff",
                    color: isAbsent ? "#fff" : "#c62828",
                    border: "2px solid #c62828", fontWeight: 700, minWidth: 100,
                  }}>
                  <Icon sx={{ mr: 0.5 }}>close</Icon> {t("valeurs.statutPresence.absent")}
                </MDButton>
              </MDBox>
            );
          })}
        </MDBox>

        {/* Footer */}
        <MDBox sx={{ p: 2, borderTop: "1px solid #e0e0e0", display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <MDButton variant="outlined" color="secondary" onClick={onClose}>
            {t("reunions.appel.fermerSansCloturer")}
          </MDButton>
          <MDButton onClick={cloturer} disabled={closing || reunion.statut === "terminee"}
            sx={{ background: "linear-gradient(135deg, #2e7d32, #43a047)", color: "#fff", fontWeight: 700 }}>
            <Icon sx={{ mr: 0.5 }}>done_all</Icon>
            {closing ? t("reunions.appel.cloture") : reunion.statut === "terminee" ? t("reunions.appel.dejaCloturee") : t("reunions.appel.terminerCloturer")}
          </MDButton>
        </MDBox>
      </MDBox>
    </Modal>
  );
}
